/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { AutocompleteItemDto, AutocompleteItemMapper, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  mergeSortByParameter,
  optimisticLockingOf,
  RestSettings,
  SlicedDataQuerySorting,
  SlicedDataSource,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import { ComponentBulkUpdateFilterDto, ComponentBulkUpdateListItemDto } from './component-bulk-update-list-item.dto';
import { ComponentBulkUpdateListItemMapper } from './component-bulk-update-list-item.mapper';
import { ComponentDetailsDto } from './component-details.dto';
import { ComponentDetailsMapper } from './component-details.mapper';
import { ComponentListItemDto } from './component-list-item.dto';
import { ComponentListItemMapper } from './component-list-item.mapper';

export type ComponentGridFilter = {
  showDeleted: boolean;
  searchString: string;
};

@Injectable()
export class ComponentRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  create(component: ComponentDetailsDto): Observable<ComponentDetailsDto> {
    return this.httpPost(
      `${this.bp}/components`,
      component,
      optimisticLockingOf(ComponentDetailsMapper));
  }

  update(component: ComponentDetailsDto): Observable<ComponentDetailsDto> {
    return this.httpPut(`${this.bp}/components/${component.id}`, component, optimisticLockingOf(ComponentDetailsMapper));
  }

  recalculate(component: ComponentDetailsDto): Observable<ComponentDetailsDto> {
    return this.httpPut(`${this.bp}/components/recalculate`, component, ComponentDetailsMapper);
  }

  clone(id: ID): Observable<ComponentDetailsDto> {
    return this.httpGet(`${this.bp}/components/clone/${id}`, ComponentDetailsMapper);
  }

  get(id: ID): Observable<ComponentDetailsDto> {
    return this.httpGet(`${this.bp}/components/${id}`, optimisticLockingOf(ComponentDetailsMapper));
  }

  findAllAsDataSource(filter: ComponentGridFilter): SlicedDataSource<ComponentListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/components`,
      { autocomplete: false, ...filter },
      void 0,
      arrayOf(ComponentListItemMapper));
  }

  getIndex(id: ID, filter: ComponentGridFilter, sorting?: SlicedDataQuerySorting): Observable<number> {
    return this.httpGet(
      mergeSortByParameter(`${this.bp}/components/${id}/index`, sorting),
      { autocomplete: false, ...filter },
      void 0,
      identity()).pipe(
      map$(({ index }) => index));
  }

  undelete(component: ComponentListItemDto): Observable<ComponentListItemDto> {
    return this.httpPut(
      `${this.bp}/components/undelete/${component.id}`,
      void 0,
      optimisticLockingOf(ComponentListItemMapper));
  }

  checkUsage(component: ComponentListItemDto): Observable<CheckUsageDto> {
    const { id, version } = component;
    return this.httpGet(
      `${this.bp}/components/check-usage/${id}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  deactivateOrDelete(component: ComponentListItemDto, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/components/${component.id}`, checkUsage, CheckUsageMapper);
  }

  // tslint:disable-next-line:max-line-length
  findAllAsBulkUpdateDataSource(filter: ComponentBulkUpdateFilterDto): SlicedDataSource<ComponentBulkUpdateListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/components/bulk-update`, filter, void 0, arrayOf(ComponentBulkUpdateListItemMapper),
    );
  }

  findAllAsBulkUpdateAutocompleteDataSource(searchString?: string): SlicedDataSource<AutocompleteItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/components`, { autocomplete: true, searchString }, void 0, arrayOf(AutocompleteItemMapper),
    );
  }
  bulkDelete(items: any[]): Observable<any> {
    return this.httpDelete(`${this.bp}/components`, items, identity());
  }

  getComponentCodes(searchString: string): Observable<string[]> {
    return this.httpGet(
      `${this.bp}/components/codes`,
      { searchString },
      void 0,
      identity()
    ).pipe(map$(response => {
      return (response as any).codes as string[];
    }));
  }
}
